// Screen: "Club Detail"
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getClubDetail, joinClub, leaveClub, kickClubMember, promoteClubMember, updateClubDues } from '../apiClient';
import EloBadge from '../components/EloBadge';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const ROLE_COLORS = { founder: '#d4af37', captain: '#58a6ff', member: '#8b949e' };
const ROLE_LABELS = { founder: 'Founder', captain: 'Captain', member: 'Member' };

export default function ClubDetailPage() {
  const { id } = useParams();
  const { user, token } = useAuth();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('members');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    const detail = await getClubDetail(id);
    if (detail) setData(detail);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
        <div style={{ color: '#8b949e', fontSize: 16 }}>Loading...</div>
      </div>
    );
  }

  const { club, members, leaderboard } = data;
  const myMembership = members.find(m => m.user_id === user?.userId);
  const isFounder = myMembership?.role === 'founder';
  const isCaptain = myMembership?.role === 'captain' || isFounder;
  const isMember = !!myMembership;

  const handleJoin = async () => {
    setError(''); setSuccess('');
    const result = await joinClub(token, id);
    if (result.success) { setSuccess('Joined!'); load(); setTimeout(() => setSuccess(''), 3000); }
    else setError(result.error || 'Failed to join');
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave this club?')) return;
    setError(''); setSuccess('');
    const result = await leaveClub(token, id);
    if (result.success) { setSuccess('Left club'); load(); setTimeout(() => setSuccess(''), 3000); }
    else setError(result.error || 'Failed to leave');
  };

  const handleKick = async (userId, nickname) => {
    if (!window.confirm(`Kick ${nickname} from the club?`)) return;
    const result = await kickClubMember(token, id, userId);
    if (result.success) { load(); } else { setError(result.error || 'Failed'); }
  };

  const handlePromote = async (userId, nickname) => {
    if (!window.confirm(`Promote ${nickname} to Captain?`)) return;
    const result = await promoteClubMember(token, id, userId);
    if (result.success) { load(); } else { setError(result.error || 'Failed'); }
  };

  return (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '60px 32px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? 16 : 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: club.color || '#238636',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, fontWeight: 700, color: '#fff'
          }}>
            {(club.name || '?')[0]}
          </div>
          <div>
            <h1 style={{ fontSize: isMobile ? 22 : 30, fontWeight: 700, margin: 0 }}>{club.name}</h1>
            <div style={{ fontSize: 12, color: '#8b949e', marginTop: 2 }}>
              {club.member_count} member{club.member_count !== 1 ? 's' : ''} &middot; {club.membership_type === 'open' ? 'Open' : 'Invite Only'}
            </div>
          </div>
        </div>
      </div>

      {club.description && (
        <div style={{
          background: '#161b22', borderRadius: 8, padding: '12px 16px',
          border: '1px solid #30363d', marginBottom: 16, color: '#8b949e', fontSize: 13
        }}>
          {club.description}
        </div>
      )}

      {error && (
        <div style={{ background: '#3d1f1f', border: '1px solid #f85149', color: '#f85149', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}
      {success && (
        <div style={{ background: '#1c3d2a', border: '1px solid #3fb950', color: '#3fb950', padding: '10px 16px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{success}</div>
      )}

      {/* Join / Leave */}
      {!isMember && club.membership_type === 'open' && (
        <button onClick={handleJoin} style={{
          width: '100%', background: '#238636', color: '#fff', border: 'none', borderRadius: 8,
          padding: '12px', fontSize: 16, fontWeight: 700, cursor: 'pointer', marginBottom: 20
        }}>Join Club</button>
      )}
      {isMember && !isFounder && (
        <button onClick={handleLeave} style={{
          width: '100%', background: '#21262d', color: '#f85149', border: '1px solid #30363d', borderRadius: 8,
          padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 20
        }}>Leave Club</button>
      )}

      {/* Dues info banner */}
      {club.dues_enabled ? (
        <div style={{
          background: '#1a1f2e', border: '1px solid #fbbf2440', borderRadius: 8,
          padding: '8px 14px', marginBottom: 14, fontSize: 12, color: '#fbbf24',
          display: 'flex', alignItems: 'center', gap: 6
        }}>
          <img src="/bucks-icon.svg" alt="" style={{ width: 12, height: 12 }} />
          Weekly dues: {club.dues_amount || 5} Bucks
        </div>
      ) : null}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #30363d' }}>
        {['members', 'leaderboard', ...(isFounder ? ['settings'] : [])].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: tab === t ? '#161b22' : 'transparent', border: 'none',
            borderBottom: tab === t ? '2px solid #58a6ff' : '2px solid transparent',
            color: tab === t ? '#e2e8f0' : '#8b949e',
            padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer'
          }}>
            {{ members: `Members (${members.length})`, leaderboard: 'Leaderboard', settings: 'Settings' }[t]}
          </button>
        ))}
      </div>

      {tab === 'members' ? (
        /* Members */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {members.map(m => (
            <div key={m.user_id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              background: '#161b22', borderRadius: 8, border: '1px solid #21262d'
            }}>
              {m.profile_picture_url ? (
                <img src={m.profile_picture_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', background: club.color || '#238636',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, color: '#fff', fontWeight: 700
                }}>
                  {(m.nickname || '?')[0]}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: m.user_id === user?.userId ? 600 : 400,
                  color: m.user_id === user?.userId ? '#58a6ff' : '#c9d1d9'
                }}>
                  {m.nickname || 'Player'}
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 600, color: ROLE_COLORS[m.role] || '#8b949e',
                  textTransform: 'uppercase'
                }}>
                  {ROLE_LABELS[m.role] || m.role}
                </span>
              </div>
              <EloBadge points={m.rank_points ?? 0} preferredSuit={m.preferred_suit || '♠'} size="sm" />
              {/* Admin actions */}
              {isCaptain && m.role === 'member' && m.user_id !== user?.userId && (
                <div style={{ display: 'flex', gap: 4 }}>
                  {isFounder && (
                    <button onClick={() => handlePromote(m.user_id, m.nickname)} style={{
                      background: 'transparent', border: '1px solid #30363d', color: '#58a6ff',
                      padding: '2px 8px', borderRadius: 4, fontSize: 10, cursor: 'pointer'
                    }}>Promote</button>
                  )}
                  <button onClick={() => handleKick(m.user_id, m.nickname)} style={{
                    background: 'transparent', border: '1px solid #30363d', color: '#f85149',
                    padding: '2px 8px', borderRadius: 4, fontSize: 10, cursor: 'pointer'
                  }}>Kick</button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : tab === 'settings' && isFounder ? (
        /* Settings (Founder only) */
        <div style={{ maxWidth: 400 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0', marginBottom: 14 }}>Club Dues</h3>
          <div style={{
            background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
            padding: '16px 18px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 14, color: '#c9d1d9' }}>Enable weekly dues</span>
              <button
                onClick={async () => {
                  const newEnabled = !club.dues_enabled;
                  const result = await updateClubDues(token, id, newEnabled, club.dues_amount || 5);
                  if (result.success) load();
                  else setError(result.error || 'Failed');
                }}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: club.dues_enabled ? '#238636' : '#30363d',
                  position: 'relative', transition: 'background 0.2s'
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3,
                  left: club.dues_enabled ? 23 : 3,
                  transition: 'left 0.2s'
                }} />
              </button>
            </div>
            {club.dues_enabled && (
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#8b949e', marginBottom: 4 }}>Amount (Bucks per week)</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[2, 5, 10, 20].map(amt => (
                    <button key={amt} onClick={async () => {
                      const result = await updateClubDues(token, id, true, amt);
                      if (result.success) load();
                    }} style={{
                      background: (club.dues_amount || 5) === amt ? '#d4af37' : '#0d1117',
                      color: (club.dues_amount || 5) === amt ? '#0d1117' : '#8b949e',
                      border: '1px solid #30363d', borderRadius: 6,
                      padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600
                    }}>
                      {amt}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Leaderboard */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {leaderboard.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#484f58', padding: 30, fontSize: 14 }}>
              No stats yet
            </div>
          ) : leaderboard.map((p, i) => (
            <div key={p.user_id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              background: '#161b22', borderRadius: 8, border: '1px solid #21262d'
            }}>
              <span style={{
                width: 24, textAlign: 'center', fontSize: i < 3 ? 16 : 12,
                color: i === 0 ? '#d4af37' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : '#8b949e',
                fontWeight: i < 3 ? 700 : 400
              }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
              </span>
              <span style={{ flex: 1, fontSize: 14, color: '#c9d1d9', fontWeight: 500 }}>{p.nickname || 'Player'}</span>
              <EloBadge points={p.rank_points ?? 0} preferredSuit={p.preferred_suit || '♠'} size="sm" />
              <span style={{ fontSize: 11, color: '#8b949e' }}>{p.games_played || 0} games</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
